import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TherapyModality, pickLang } from '@/hooks/useTherapyModalities';
import { localizeDomainFlags } from '@/utils/therapy-vocab';
import { TherapyModalityStats } from '@/hooks/useTherapyModalityStats';
import { useTeenMode } from '@/hooks/useTeenMode';

import { ArrowUpRight } from 'lucide-react';

interface TherapyCardProps {
  therapy: TherapyModality;
  stats?: TherapyModalityStats;
  eventCount?: number;
}

// A deterministic background variant per therapy so each card has its own
// quiet visual identity without becoming busy.
const BG_VARIANTS = [
  'radial-gradient(circle at 72% 18%, hsla(45, 92%, 48%, 0.35) 0%, transparent 52%), radial-gradient(circle at 12% 82%, hsla(32, 45%, 64%, 0.30) 0%, transparent 45%)',
  'radial-gradient(circle at 18% 22%, hsla(28, 55%, 60%, 0.32) 0%, transparent 50%), radial-gradient(circle at 82% 78%, hsla(45, 90%, 52%, 0.25) 0%, transparent 48%)',
  'radial-gradient(circle at 85% 30%, hsla(38, 62%, 58%, 0.30) 0%, transparent 55%), radial-gradient(circle at 20% 75%, hsla(45, 95%, 50%, 0.22) 0%, transparent 42%)',
  'radial-gradient(circle at 50% 12%, hsla(45, 88%, 50%, 0.28) 0%, transparent 50%), radial-gradient(circle at 50% 90%, hsla(30, 40%, 58%, 0.26) 0%, transparent 50%)',
  'radial-gradient(circle at 28% 30%, hsla(42, 70%, 55%, 0.32) 0%, transparent 50%), radial-gradient(circle at 78% 72%, hsla(25, 50%, 62%, 0.22) 0%, transparent 45%)',
];

const hashIndex = (id: string, mod: number) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % mod;
};

const TherapyCard: React.FC<TherapyCardProps> = ({ therapy, stats, eventCount = 0 }) => {
  const { t, i18n } = useTranslation('therapyLibrary');
  const isZh = i18n.language === 'zh';
  const { teenMode } = useTeenMode();


  const name = pickLang(isZh, therapy.name_cn, therapy.name_en);
  const mechanism = pickLang(isZh, therapy.mechanism_cn, therapy.mechanism_en);
  const streamType = therapy.stream_type
    ? t(`streamTypes.${therapy.stream_type}`, {
        defaultValue: (isZh && therapy.stream_type_cn) || therapy.stream_type,
      })
    : null;
  const targetDomain = localizeDomainFlags(isZh, therapy);

  const evidenceLabel = therapy.evidence_level
    ? t(`evidenceLevels.${therapy.evidence_level}`, { defaultValue: `Lvl. ${therapy.evidence_level}` })
    : null;

  const bg = BG_VARIANTS[hashIndex(therapy.id, BG_VARIANTS.length)];
  const refId = `REF.${String(hashIndex(therapy.id, 99) + 1).padStart(2, '0')}-MOD-${String(hashIndex(therapy.id + 'm', 999) + 1).padStart(3, '0')}`;

  const practitionerCount = stats?.practitioner_count || 0;
  const bookable = practitionerCount > 0;
  const hasEvents = eventCount > 0;

  return (
    <Link
      id={`therapy-card-${therapy.id}`}
      to={`/framework/${therapy.id}`}
      className="group relative overflow-hidden rounded-sm border border-[hsl(var(--primary)/0.18)] bg-[#FDFAF6] shadow-sm hover:shadow-xl hover:border-[hsl(var(--primary)/0.4)] transition-all duration-500 flex flex-col min-h-[280px]"
    >
      {/* Abstract editorial background */}
      <div
        aria-hidden
        className="absolute inset-0 z-0 opacity-40 mix-blend-multiply transition-transform duration-1000 group-hover:scale-110 pointer-events-none"
        style={{ background: bg }}
      />

      {/* Content */}
      <div className="relative z-10 p-7 md:p-9 flex flex-col h-full">
        {/* Metadata header */}
        <header className="flex items-start justify-between mb-8 gap-3">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {streamType && (
              <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-stone-500 font-sans">
                {streamType}
              </span>
            )}
            {evidenceLabel && (
              <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-stone-400 font-sans">
                {evidenceLabel}
              </span>
            )}
          </div>
          <div className="w-8 h-px bg-[hsl(var(--primary)/0.6)] flex-shrink-0 mt-2" />
        </header>

        {/* Title */}
        {/* Availability badges */}
        {(bookable || hasEvents || (teenMode && therapy.teen_status === 'restricted')) && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {bookable && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-sans font-medium text-stone-800 bg-[hsl(var(--primary)/0.18)] border border-[hsl(var(--primary)/0.35)] px-2 py-0.5 rounded-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
                {t('badgeBookable', { defaultValue: 'Bookable' })}
                {practitionerCount > 0 && (
                  <span className="ml-1 text-stone-600 normal-case tracking-normal">
                    · {practitionerCount}
                  </span>
                )}
              </span>
            )}
            {hasEvents && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-sans font-medium text-stone-700 bg-stone-100 border border-stone-300 px-2 py-0.5 rounded-sm">
                {t('badgeUpcomingEvent', { defaultValue: 'Upcoming event' })}
                <span className="ml-1 text-stone-500 normal-case tracking-normal">· {eventCount}</span>
              </span>
            )}
            {teenMode && therapy.teen_status === 'restricted' && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-sans font-medium text-stone-700 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-sm">
                {t('teen.badgeGuardian', { defaultValue: 'Guardian required' })}
              </span>
            )}
          </div>
        )}


        {/* Title */}
        <div className="mb-6">
          <h2 className="font-serif italic font-medium text-2xl md:text-3xl leading-tight text-stone-900 tracking-tight mb-2">
            {name}
          </h2>
          {therapy.abbreviation && (
            <p className="text-stone-400 font-sans text-[10px] tracking-[0.2em] uppercase">
              {therapy.abbreviation}
            </p>
          )}
        </div>

        {/* Core idea — mechanism as the concept */}
        <div className="mt-auto">
          {mechanism && (
            <blockquote className="mb-6 border-l-2 border-[hsl(var(--primary)/0.3)] pl-4">
              <p className="font-serif italic text-sm md:text-base text-stone-700 leading-relaxed line-clamp-3">
                “{mechanism}”
              </p>
            </blockquote>
          )}

          <footer className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center text-[11px] font-semibold tracking-[0.2em] uppercase text-stone-900 group-hover:text-[hsl(var(--primary))] transition-colors font-sans">
              {t('readEntry', { defaultValue: 'Read entry' })}
              <ArrowUpRight className="w-3.5 h-3.5 ml-2 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
            {targetDomain && (
              <span className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-sans truncate max-w-[55%] text-right">
                {targetDomain}
              </span>
            )}
          </footer>
        </div>
      </div>

      {/* Catalog reference id — decorative */}
      <div className="absolute bottom-3 right-3 opacity-25 pointer-events-none">
        <span className="text-[8px] font-mono text-stone-900 tracking-wider">{refId}</span>
      </div>
    </Link>
  );
};

export default TherapyCard;
